import { useMemo, useCallback } from 'react'
import {
  FEED_FILTER_INIT_VALUES,
  FEED_FILTER_STORAGE_NAME,
  FOLLOWED_FILTER_OPTIONS, RANGE_FILTER_OPTIONS
} from 'shared/consts/filter'
import { isNestedObjEqual } from 'shared/utils'
import { getDateRange } from '../utils/filters'
import { FeedFilterKeys } from '../types'
import { FeedFilters, Tag } from 'shared/__gql-types'
import { TSelectOption } from 'shared/types'

export const useFeedFilter = (filters: any, setFilters: (v: FeedFilters) => void, unitOptions: TSelectOption[], zoneOptions: TSelectOption[]) => {
  const onSetFilter = useCallback((value: any) => {
    setFilters(value)
    localStorage.setItem(FEED_FILTER_STORAGE_NAME, JSON.stringify(value))
  }, [setFilters])

  const onToggleFollowed = useCallback(<T extends {}>(key: string) => (value: T) => {
    onSetFilter({ ...filters, [key]: value })
  }, [filters, onSetFilter])

  const isResetDisabled = isNestedObjEqual(filters, FEED_FILTER_INIT_VALUES)

  const onReset = (key?: FeedFilterKeys, label?: string) => {
    if (!key) onSetFilter(FEED_FILTER_INIT_VALUES)
    else onSetFilter({ ...filters, ...(
      key === 'tags' && label ?
        { tags: filters.tags.filter((t: Tag) => t.label !== label) } :
        { [key]: FEED_FILTER_INIT_VALUES[key] }
    )})
  }

  const getLabel = useCallback((key: FeedFilterKeys) => {
    if (key === 'followed') {
      return FOLLOWED_FILTER_OPTIONS.find((el) => el.value === filters[key])?.label
    }
    if (key === 'range') {
      return filters[key].includes('from') ?
        getDateRange(filters[key]) :
        RANGE_FILTER_OPTIONS.find((el) => el.value === filters[key])?.label
    }
    if (key === 'unit_id') {
      return unitOptions.find((el) => el.value === filters[key])?.label
    }
    if (key === 'label_id') {
      return zoneOptions.find((el) => el.value === filters[key])?.label
    }
    return key
  }, [unitOptions, zoneOptions, filters])

  const selectedFilters = useMemo(() =>
    (Object.keys(filters) as FeedFilterKeys[]).reduce((res: any, item) => {
      if (isNestedObjEqual(filters[item], FEED_FILTER_INIT_VALUES[item])) return res

      if (item === 'tags') {
        return [
          ...res,
          ...(filters?.[item]?.map((el: any) => ({ key: item, label: getLabel(el.label) })) ?? [])
        ]
      }

      return [...res, { key: item, label: getLabel(item) }]
    }, []),[filters, getLabel])

  return {
    selectedFilters,
    onSetFilter,
    onToggleFollowed,
    isResetDisabled,
    onReset
  }
}
